import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { ListPageLayout } from "@dashboard/components/Layouts";
import {
  useOpeningHoursQuery,
  useOpeningHoursUpdateMutation,
  useOpeningSpecialDayDeleteMutation,
  useOpeningSpecialDaySaveMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { completeIntervals, formatIntervals, Interval, weekdayName } from "./hours";
import { IntervalsEditor } from "./IntervalsEditor";
import { openingHoursMessages as m } from "./messages";

const sourceMessage = {
  SPECIAL: m.sourceSpecial,
  HOLIDAY: m.sourceHoliday,
  WEEKDAY: m.sourceWeekday,
} as const;

const cell = { padding: "6px 12px 6px 0", verticalAlign: "top" } as const;
const rowStyle = { borderTop: "1px solid var(--mu-colors-border-default1, #e0e3e8)" } as const;
const toForm = (intervals: readonly Interval[] | null): Interval[] =>
  (intervals ?? []).map(({ open, close }) => ({ open, close }));

export const OpeningHours = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const { data, refetch } = useOpeningHoursQuery({ fetchPolicy: "network-only" });
  const [updateHours, updateResult] = useOpeningHoursUpdateMutation();
  const [saveSpecial, saveSpecialResult] = useOpeningSpecialDaySaveMutation();
  const [deleteSpecial] = useOpeningSpecialDayDeleteMutation();

  const hours = data?.openingHours;
  const [weekly, setWeekly] = useState<Interval[][]>(Array.from({ length: 7 }, () => []));
  const [holiday, setHoliday] = useState<Interval[]>([]);
  const [specialDate, setSpecialDate] = useState("");
  const [specialLabel, setSpecialLabel] = useState("");
  const [specialIntervals, setSpecialIntervals] = useState<Interval[]>([]);

  useEffect(() => {
    if (hours) {
      setWeekly((hours.weekly ?? []).map(day => toForm(day.intervals)));
      setHoliday(toForm(hours.holiday));
    }
  }, [hours]);

  const closedText = intl.formatMessage(m.closed);
  const pickedEffective = hours?.upcoming?.find(day => day.date === specialDate);

  const handleSave = async () => {
    const result = await updateHours({
      variables: {
        weekly: weekly.map((intervals, weekday) => ({
          weekday,
          intervals: completeIntervals(intervals),
        })),
        holiday: completeIntervals(holiday),
      },
    });

    if (result.data?.openingHoursUpdate?.errors.length) {
      notify({ status: "error", text: intl.formatMessage(m.saveError) });

      return;
    }

    notify({ status: "success", text: intl.formatMessage(m.saved) });
    await refetch();
  };

  const handlePickDate = (date: string) => {
    setSpecialDate(date);

    const existing = hours?.specialDays?.find(day => day.date === date);

    setSpecialLabel(existing?.label ?? "");
    setSpecialIntervals(
      existing
        ? toForm(existing.intervals)
        : toForm(hours?.upcoming?.find(day => day.date === date)?.intervals ?? []),
    );
  };

  const handleSaveSpecial = async () => {
    const result = await saveSpecial({
      variables: {
        date: specialDate,
        label: specialLabel,
        intervals: completeIntervals(specialIntervals),
      },
    });

    if (result.data?.openingSpecialDaySave?.errors.length) {
      notify({ status: "error", text: intl.formatMessage(m.saveError) });

      return;
    }

    notify({ status: "success", text: intl.formatMessage(m.specialSaved) });
    setSpecialDate("");
    setSpecialLabel("");
    setSpecialIntervals([]);
    await refetch();
  };

  const handleDeleteSpecial = async (id: string) => {
    await deleteSpecial({ variables: { id } });
    notify({ status: "success", text: intl.formatMessage(m.specialDeleted) });
    await refetch();
  };

  return (
    <ListPageLayout>
      <TopNav title={intl.formatMessage(m.sectionName)} withoutBorder>
        {hours && (
          <Box display="flex" justifyContent="flex-end" __flex={1}>
            <Text size={2} color={hours.isOpenNow ? "success1" : "default2"}>
              {intl.formatMessage(hours.isOpenNow ? m.openNow : m.closedNow)}
            </Text>
          </Box>
        )}
      </TopNav>
      <Box display="flex" flexDirection="column" gap={5} padding={6}>
        <Text size={2} color="default2">
          {intl.formatMessage(m.intro)}
        </Text>

        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(m.weeklyTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="flex" flexDirection="column" gap={4}>
              {weekly.map((intervals, weekday) => (
                <Box key={weekday} display="flex" gap={4} style={{ flexWrap: "wrap" }}>
                  <Box style={{ width: 160 }}>
                    <Text size={3} fontWeight="bold">
                      {weekdayName(weekday, intl.locale)}
                    </Text>
                  </Box>
                  <IntervalsEditor
                    testId={`opening-hours-day-${weekday}`}
                    value={intervals}
                    onChange={next =>
                      setWeekly(weekly.map((day, index) => (index === weekday ? next : day)))
                    }
                  />
                </Box>
              ))}
              <Box display="flex" gap={4} style={{ flexWrap: "wrap" }}>
                <Box style={{ width: 160 }}>
                  <Text size={3} fontWeight="bold">
                    {intl.formatMessage(m.holidayRow)}
                  </Text>
                </Box>
                <Box display="flex" flexDirection="column" gap={2}>
                  <IntervalsEditor
                    testId="opening-hours-holiday"
                    value={holiday}
                    onChange={setHoliday}
                  />
                  <Text size={1} color="default2" style={{ maxWidth: 560 }}>
                    {intl.formatMessage(m.holidayHelp)}
                  </Text>
                </Box>
              </Box>
              <Box>
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={updateResult.loading}
                  data-test-id="opening-hours-save"
                >
                  {intl.formatMessage(m.save)}
                </Button>
              </Box>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>

        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(m.specialTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box display="flex" flexDirection="column" gap={4}>
              <Text size={2} color="default2" style={{ maxWidth: 640 }}>
                {intl.formatMessage(m.specialHelp)}
              </Text>
              <Box display="flex" gap={3} style={{ flexWrap: "wrap" }}>
                <Box style={{ width: 200 }}>
                  <Input
                    type="date"
                    size="small"
                    label={intl.formatMessage(m.pickDate)}
                    value={specialDate}
                    onChange={event => handlePickDate(event.target.value)}
                    data-test-id="opening-special-date"
                  />
                </Box>
                <Box style={{ width: 280 }}>
                  <Input
                    size="small"
                    label={intl.formatMessage(m.specialLabel)}
                    value={specialLabel}
                    onChange={event => setSpecialLabel(event.target.value)}
                  />
                </Box>
              </Box>
              {specialDate && (
                <>
                  {pickedEffective && (
                    <Text size={2} color="default2">
                      {intl.formatMessage(m.normallyThatDay, {
                        hours: formatIntervals(pickedEffective.intervals ?? [], closedText),
                      })}
                      {pickedEffective.label ? ` (${pickedEffective.label})` : ""}
                    </Text>
                  )}
                  <IntervalsEditor
                    testId="opening-special-intervals"
                    value={specialIntervals}
                    onChange={setSpecialIntervals}
                  />
                  <Box>
                    <Button
                      variant="primary"
                      onClick={handleSaveSpecial}
                      disabled={saveSpecialResult.loading}
                      data-test-id="opening-special-save"
                    >
                      {intl.formatMessage(m.saveSpecial)}
                    </Button>
                  </Box>
                </>
              )}
              {hours?.specialDays?.length === 0 && (
                <Text size={2} color="default2">
                  {intl.formatMessage(m.noSpecial)}
                </Text>
              )}
              {!!hours?.specialDays?.length && (
                <Box style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <tbody>
                      {(hours.specialDays ?? []).map(day => (
                        <tr key={day.id} style={rowStyle}>
                          <td style={cell}>
                            <Text size={2} fontWeight="bold">
                              {new Date(`${day.date}T12:00:00Z`).toLocaleDateString(intl.locale, {
                                dateStyle: "full",
                                timeZone: "UTC",
                              })}
                            </Text>
                            {day.label && (
                              <>
                                <br />
                                <Text size={1} color="default2">
                                  {day.label}
                                </Text>
                              </>
                            )}
                          </td>
                          <td style={cell}>
                            <Text size={2}>{formatIntervals(day.intervals ?? [], closedText)}</Text>
                          </td>
                          <td style={{ ...cell, textAlign: "right" }}>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handlePickDate(day.date)}
                            >
                              {intl.formatMessage(m.edit)}
                            </Button>{" "}
                            <Button
                              variant="tertiary"
                              size="small"
                              onClick={() => handleDeleteSpecial(day.id)}
                            >
                              {intl.formatMessage(m.remove)}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}
            </Box>
          </DashboardCard.Content>
        </DashboardCard>

        <DashboardCard>
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(m.upcomingTitle)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Box style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {(hours?.upcoming ?? []).slice(0, 14).map(day => (
                    <tr key={day.date} style={rowStyle}>
                      <td style={cell}>
                        <Text size={2}>
                          {new Date(`${day.date}T12:00:00Z`).toLocaleDateString(intl.locale, {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            timeZone: "UTC",
                          })}
                        </Text>
                      </td>
                      <td style={cell}>
                        <Text size={2}>{formatIntervals(day.intervals ?? [], closedText)}</Text>
                      </td>
                      <td style={cell}>
                        <Text size={1} color="default2">
                          {intl.formatMessage(sourceMessage[day.source])}
                          {day.label ? ` · ${day.label}` : ""}
                        </Text>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
      </Box>
    </ListPageLayout>
  );
};
